/**
 * @file components/PublicTopNav.js
 * @description Slim top nav for PUBLIC (logged-out) pages — Landing, Pricing, Features.
 * Intentionally separate from Navbar.js, which is for authenticated users only.
 * Mobile-responsive: collapses to a hamburger on <640px.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const linkBase = {
  textDecoration: 'none',
  fontSize: '0.9375rem',
  fontWeight: 500,
  padding: '0.5rem 0.75rem',
  borderRadius: '6px',
  transition: 'background-color 0.15s, color 0.15s',
};

function PublicTopNav() {
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    { to: '/features', label: 'Features' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/build-log', label: 'Build Log' },
    { to: '/login', label: 'Login' },
  ];

  return (
    <nav
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 1.5rem',
        borderBottom: '1px solid #E5E5E5',
        backgroundColor: '#FFFFFF',
        position: 'relative',
        zIndex: 50,
      }}
    >
      <Link
        to="/"
        style={{
          fontSize: '1.125rem',
          fontWeight: 700,
          color: '#000',
          textDecoration: 'none',
          letterSpacing: '-0.01em',
        }}
      >
        Inspection Butler
      </Link>

      {/* Desktop links */}
      <div
        className="public-nav-desktop"
        style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
      >
        {navLinks.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            style={{ ...linkBase, color: '#374151' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F3F4F6')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            {l.label}
          </Link>
        ))}
        <Link
          to="/register"
          style={{
            ...linkBase,
            backgroundColor: '#2563EB',
            color: '#FFFFFF',
            marginLeft: '0.5rem',
            fontWeight: 600,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1D4ED8')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2563EB')}
        >
          Join
        </Link>
      </div>

      {/* Mobile hamburger */}
      <button
        type="button"
        aria-label="Open menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
        className="public-nav-hamburger"
        style={{
          display: 'none',
          background: 'none',
          border: 'none',
          padding: '0.5rem',
          cursor: 'pointer',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          {menuOpen ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
      </button>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div
          className="public-nav-mobile-dropdown"
          style={{
            display: 'none',
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #E5E5E5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            flexDirection: 'column',
            padding: '0.5rem',
            gap: '0.25rem',
            zIndex: 49,
          }}
        >
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              style={{
                ...linkBase,
                color: '#374151',
                display: 'block',
                padding: '0.75rem 1rem',
              }}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/register"
            onClick={() => setMenuOpen(false)}
            style={{
              ...linkBase,
              backgroundColor: '#2563EB',
              color: '#FFFFFF',
              fontWeight: 600,
              textAlign: 'center',
              display: 'block',
              padding: '0.75rem 1rem',
              marginTop: '0.25rem',
            }}
          >
            Join
          </Link>
        </div>
      )}

      {/* Responsive styles — toggle desktop/mobile visibility */}
      <style>{`
        @media (max-width: 640px) {
          .public-nav-desktop { display: none !important; }
          .public-nav-hamburger { display: block !important; }
          .public-nav-mobile-dropdown { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}

export default PublicTopNav;
